/**
 * NX Integration Type Definitions
 * TypeScript interfaces and types for NX WebSocket communication
 */

// WebSocket connection status
export type NXConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

// Code execution result
// NX returns JSON `{ status: 0|1, message }` (0 = success, 1 = failure); mapped to success here.
export interface NXExecutionResult {
    success: boolean;
    /** NX status code: 0 = success, 1 = failure/error */
    status?: 0 | 1;
    message: string;
    data?: any;
    timestamp?: Date;
}

// WebSocket message types
export interface NXWebSocketMessage {
    type: 'execute' | 'query' | 'response' | 'error';
    data: any;
    messageId?: string;
    timestamp?: Date;
}

// NX part information structure
export interface NXPartInfo {
    filePath: string;
    fileName: string;
    totalBodies: number;
    visibleBodies: number;
    dimensions?: number[];
    attributes?: NXPartAttribute[];
    features?: NXFeature[];
    expressions?: NXExpression[];
}

// NX part attribute
export interface NXPartAttribute {
    title: string;
    type: string;
    value: string;
}

// NX feature information
export interface NXFeature {
    name: string;
    featureName: string;
    featureType: string;
}

// NX expression information
export interface NXExpression {
    name: string;
    value: number | string;
}

// Code validation result
export interface CodeValidationResult {
    isValid: boolean;
    warnings: string[];
    suggestions?: string[];
}

// NX execution context
export interface NXExecutionContext {
    code: string;
    timestamp: Date;
    result?: NXExecutionResult;
    duration?: number;
}

// NX WebSocket service callbacks
export interface NXWebSocketCallbacks {
    onConnectionChange?: (status: NXConnectionStatus) => void;
    onExecutionResult?: (result: NXExecutionResult) => void;
    onError?: (error: string) => void;
    onMessage?: (message: string) => void;
}

// NX service configuration
export interface NXServiceConfig {
    websocketUrl: string;
    reconnectAttempts: number;
    reconnectDelay: number;
    executionTimeout: number;
}

// Chat message with NX context
export interface NXChatMessage {
    id: string;
    sender: 'user' | 'ai' | 'nx';
    text: string;
    timestamp: Date;
    code?: string;
    executionResult?: NXExecutionResult;
    nxContext?: NXPartInfo;
}

// NX integration state
export interface NXIntegrationState {
    connected: boolean;
    connectionStatus: NXConnectionStatus;
    globalCode: string;
    lastExecutionResult?: NXExecutionResult;
    partInfo?: NXPartInfo;
}

// Default NX service configuration
export const DEFAULT_NX_CONFIG: NXServiceConfig = {
    websocketUrl: 'ws://localhost:11189/', // Will be dynamically determined by nxWebSocketService
    reconnectAttempts: 5, // Increased from 3 for better reliability
    reconnectDelay: 2000,
    executionTimeout: 1800000 // Extended to 30 minutes for complex operations
};

/**
 * Helper function to build WebSocket URL with dynamic port
 * This is used by nxWebSocketService for actual connections
 * @param port - Port number to connect to
 * @returns Complete WebSocket URL
 */
export const buildNXWebSocketUrl = (port: number): string => {
    return `ws://localhost:${port}/`;
};

// NX WebSocket fixed-port polling configuration.
// The built-in NX WebSocket server uses port 11189 by default; if occupied, NX switches ports.
// Connection strategy: start polling from 11189, add +1000 on each failed attempt, up to 3 increments.
export const NX_WS_BASE_PORT = 11189;
export const NX_WS_PORT_INCREMENT = 1000;
export const NX_WS_MAX_INCREMENTS = 3;

// localStorage key for the last successful port
export const NX_WS_LAST_PORT_KEY = 'nxWebSocketLastPort';

/**
 * Build the ordered list of candidate ports to poll when connecting to NX.
 * Starts at NX_WS_BASE_PORT, then adds NX_WS_PORT_INCREMENT up to NX_WS_MAX_INCREMENTS times.
 * If a preferredPort is given (e.g. the last successful port from localStorage), it is moved to
 * the front of the list so reconnects try it first; the remaining fixed ports stay as fallbacks
 * in case NX has since switched ports.
 * @param preferredPort - Optional port to try first
 * @returns e.g. [12189, 11189, 13189, 14189] when preferredPort is 12189
 */
export const getNXPortCandidates = (preferredPort?: number): number[] => {
    const ports: number[] = [];
    for (let i = 0; i <= NX_WS_MAX_INCREMENTS; i++) {
        ports.push(NX_WS_BASE_PORT + i * NX_WS_PORT_INCREMENT);
    }
    if (preferredPort && ports.includes(preferredPort)) {
        return [preferredPort, ...ports.filter((p) => p !== preferredPort)];
    }
    return ports;
};

// NX message types for chat display
export type NXMessageType = 'success' | 'error' | 'warning' | 'info' | 'code' | 'query';

// NX execution modes
export type NXExecutionMode = 'direct' | 'query' | 'test';

// Types are already exported above, no need to re-export
